import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { ReportErrorButton } from "@/components/ReportError";
import { MapPin, Search, CheckCircle2, CircleDashed, Clock3 } from "lucide-react";

const LEVELS = {
  full: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "✅" },
  ward_pending: { icon: Clock3, cls: "bg-amber-50 text-amber-900 border-amber-300", dot: "🟡" },
  coming_soon: { icon: CircleDashed, cls: "bg-slate-50 text-slate-600 border-slate-200", dot: "⚪" },
};

export default function CoveragePublic() {
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/public/coverage/availability-summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      if (!q.trim()) { setResults(null); return; }
      setBusy(true);
      api.get("/public/coverage/availability", { params: { q: q.trim() } })
        .then((r) => setResults(r.data)).catch(() => setResults(null))
        .finally(() => setBusy(false));
    }, 350);
    return () => clearTimeout(id);
  }, [q]);

  return (
    <div className="wyv-container py-10 sm:py-14">
      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Coverage</Badge>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="coverage-title">
        Where WitnessYourVote is available
      </h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Every Ontario municipality has an information page. Ward maps appear only where official
        boundary data has been imported and verified — we never guess boundaries.
      </p>
      <div className="mt-3">
        <ReportErrorButton context="Coverage page" defaultCategory="incorrect_ward_boundary" />
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6" data-testid="coverage-summary">
          {[
            ["Ontario municipalities", summary.municipalities_total],
            ["Municipality pages", summary.municipality_pages],
            ["Ward maps loaded", summary.ward_maps_loaded],
            ["Ward maps verified", summary.ward_maps_verified],
          ].map(([label, value]) => (
            <Card key={label}><CardContent className="p-4">
              <p className="font-heading font-bold text-2xl text-foreground tabular-nums">{value}</p>
              <p className="text-[11px] text-muted-foreground">{label}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      <div className="mt-8 max-w-xl">
        <Label htmlFor="coverage-search" className="text-sm font-medium text-foreground">Enter your municipality</Label>
        <div className="relative mt-1.5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input id="coverage-search" className="pl-9" placeholder="e.g. Kawartha Lakes, Guelph, Barrie…"
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="coverage-search-input" />
        </div>
      </div>

      <div className="mt-6 space-y-3 max-w-2xl">
        {busy && <Skeleton className="h-20" />}
        {results && results.results.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="coverage-no-results">
            No Ontario municipality matched “{q}”. Check the spelling — every Ontario municipality is listed.
          </p>
        )}
        {results?.results.map((r) => {
          const L = LEVELS[r.level] || LEVELS.coming_soon;
          const Icon = L.icon;
          return (
            <Card key={r.slug} data-testid={`coverage-result-${r.slug}`}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-foreground inline-flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-accent shrink-0" /> {r.name}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{r.type || "Municipality"}{r.ward_count ? ` · ${r.ward_count} wards mapped` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`${L.cls} gap-1 text-[11px]`} data-testid={`coverage-level-${r.slug}`}>
                    <Icon className="h-3 w-3" /> {L.dot} {r.label}
                  </Badge>
                  <Link to={`/ontario/${r.slug}`} className="text-xs text-primary hover:underline" data-testid={`coverage-page-link-${r.slug}`}>View page →</Link>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-10 max-w-2xl rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs text-muted-foreground leading-relaxed" data-testid="coverage-legend">
          <strong className="text-foreground">✅ Full Coverage</strong> — official ward boundaries imported and verified.<br />
          <strong className="text-foreground">🟡 Municipality Coverage — Ward Map Pending</strong> — the municipality page is live, but ward boundaries are awaiting official data or verification.<br />
          <strong className="text-foreground">⚪ Coming Soon</strong> — an information page exists; ward mapping has not started.<br />
          {summary?.note}
        </p>
      </div>
    </div>
  );
}
